import React, { useContext } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { getDuplicateApps } from "go-app/utils";
import FormatDate, { formatFormDateToString } from "go-core/components/Formatters/FormatDate";
import { redirectToApplication } from "go-core/utils/utils";
import AppEntityStatus from "../../../../components/Common/EntityStatus/EntityStatus";
import { ReactComponent as CalendarSuccessSVG } from "../../../../images/svg/calendar-success.svg";
import { ReactComponent as TimerSVG } from "../../../../images/svg/organization/clock.svg";
import { ReactComponent as CloudArrowDownSVG } from "../../../../images/svg/organization/cloud-arrow-down.svg";
import { ReactComponent as FlagSVG } from "../../../../images/svg/organization/flag.svg";
import { ReactComponent as CalendarDangerSVG } from "../../../../images/svg/subscriptions/calendar-danger.svg";
import { OrganizationApi, OrganizationApplicationApi } from "../../../../services/Api/types";
import { ConfigContext } from "../../../../services/Config/context";
import { getNewAppsVersion } from "../../../../utils/AppVersion";

interface Props {
	organization: OrganizationApi;
}

const OrganizationListApplicationsColumn = ({ organization }: Props) => {
	const { t } = useTranslation("admin");
	const configService = useContext(ConfigContext);
	const applicationsFromConfig = [...configService.get().apps];
	const duplicateApplications = getDuplicateApps(applicationsFromConfig);
	const filteredApplications = getNewAppsVersion(duplicateApplications, applicationsFromConfig);

	const drawLicenseDate = (app: OrganizationApplicationApi) => {
		const result: string[] = [];
		if (app.license_created_at) result.push(FormatDate(app.license_created_at).split(",")[0]);
		if (app.license_expires_at) result.push(FormatDate(app.license_expires_at).split(",")[0]);
		if (
			app.status !== "REMOVED" &&
			(app.license_created_at || app.license_expires_at) &&
			formatFormDateToString(app.license_expires_at).getTime() >= new Date().getTime()
		) {
			return (
				<>
					<CalendarSuccessSVG />
					<span className="date-label text-success">{result?.join(" - ")}</span>
				</>
			);
		} else if (
			(app.status === "REMOVED" && (app.license_created_at || app.license_expires_at)) ||
			formatFormDateToString(app.license_expires_at).getTime() < new Date().getTime()
		) {
			return (
				<>
					<CalendarDangerSVG />
					<span className="date-label text-danger">{result?.join(" - ")}</span>
				</>
			);
		}
	};

	return (
		<div className="d-flex flex-column">
			{organization.applications?.map((app, index) => {
				const appFromConfig = filteredApplications.find(
					(uniqueApplication) => uniqueApplication.id.toUpperCase() === app.slug
				);
				const redirectURL = appFromConfig?.url;

				return (
					<div className="d-flex flex-column" key={app.id}>
						<div className="d-flex align-items-center">
							<a
								className="social-icon text-xs-center me-1"
								target={"_blank"}
								href={redirectToApplication(redirectURL ?? "", organization.company_id)}
								rel="noreferrer"
							>
								{app.name}
							</a>
							<AppEntityStatus status={organization.status} />
						</div>
						<div className="d-flex flex-column">
							{app.created_at && (
								<div className="d-flex align-items-center">
									<OverlayTrigger
										placement="top"
										overlay={
											<Tooltip id={`tooltip-created-at-${index}`}>
												{t("lib:common.word.created_at")}
											</Tooltip>
										}
									>
										<CloudArrowDownSVG />
									</OverlayTrigger>
									<span className="date-label">{FormatDate(app.created_at).split(",")[0]}</span>
								</div>
							)}
							{app.activated_at && (
								<div className="d-flex align-items-center mt-1">
									<OverlayTrigger
										placement="top"
										overlay={
											<Tooltip id={`tooltip-activated-at-${index}`}>
												{t("modules.organization.field.activated_at.title")}
											</Tooltip>
										}
									>
										<FlagSVG />
									</OverlayTrigger>
									<span className="date-label">{FormatDate(app.activated_at).split(",")[0]}</span>
								</div>
							)}
							{app.last_used_at && (
								<div className="d-flex align-items-center mt-1">
									<OverlayTrigger
										placement="top"
										overlay={
											<Tooltip id={`tooltip-last-used-at-${index}`}>
												{t("modules.organization.field.last_used_at.title")}
											</Tooltip>
										}
									>
										<TimerSVG />
									</OverlayTrigger>
									<span className="date-label">{FormatDate(app.last_used_at).split(",")[0]}</span>
								</div>
							)}
							{<div className="d-flex text-nowrap mt-1">{drawLicenseDate(app)}</div>}
						</div>
					</div>
				);
			})}
		</div>
	);
};

export default OrganizationListApplicationsColumn;
